import { useEffect } from 'react';

type UseAriaDismissParams = {
  containerRef: React.RefObject<Node | null>;
  dropdownRef?: React.RefObject<Node | null>;
  open: boolean;
  onDismiss: () => void;
};

/**
 * useAriaDismiss
 * Registers document-level listeners to dismiss a popup when the user
 * clicks outside, tabs away (focusin), switches tabs (visibilitychange)
 * or the window blurs.
 */
export function useAriaDismiss({ containerRef, dropdownRef, open, onDismiss }: UseAriaDismissParams) {
  useEffect(() => {
    if (!open) return;

    function isEventInside(evt: Event) {
      if (!containerRef.current) return false;
      const anyEvt = evt as any;
      const path: EventTarget[] | undefined =
        (typeof anyEvt.composedPath === 'function' && anyEvt.composedPath()) || anyEvt.path;

      if (Array.isArray(path)) {
        return path.some(
          (p) =>
            p === containerRef.current ||
            p === dropdownRef?.current ||
            (p instanceof Node &&
              (containerRef.current?.contains(p as Node) || dropdownRef?.current?.contains(p as Node))),
        );
      }

      return (
        evt.target instanceof Node &&
        (containerRef.current.contains(evt.target as Node) || dropdownRef?.current?.contains(evt.target as Node))
      );
    }

    function onDocEvent(e: Event) {
      if (!isEventInside(e)) onDismiss();
    }

    function onFocusIn(e: FocusEvent) {
      if (!isEventInside(e)) onDismiss();
    }

    function onWindowBlur() {
      onDismiss();
    }

    function onVisibilityChange() {
      if (document.hidden) onDismiss();
    }

    document.addEventListener('pointerdown', onDocEvent, true);
    document.addEventListener('touchstart', onDocEvent, true);
    document.addEventListener('focusin', onFocusIn, true);
    window.addEventListener('blur', onWindowBlur);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('pointerdown', onDocEvent, true);
      document.removeEventListener('touchstart', onDocEvent, true);
      document.removeEventListener('focusin', onFocusIn, true);
      window.removeEventListener('blur', onWindowBlur);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [containerRef, dropdownRef, open, onDismiss]);
}

export default useAriaDismiss;
